// What a draft room runs on, besides the draft's own state (BACKLOG D10,
// PLAN-2026-10-14 §1, MOCK_DRAFT.md §4): the seats, the board with what a
// drafter reads about every player, and the engine's context as plain data.
//
// `@fsp/league` prepares one from a league's rows for the live room; the mock
// draft's kit is one made for a single person, with a bot in every other seat.

import type { DraftMode, DraftSeasonLine, LeagueRef, PlayerSummary, TeamRef } from '@fsp/domain';
import type {
  AiManager,
  AuctionRules,
  ClockExpiry,
  DraftContextParts,
  DraftFacts,
  DraftKeeper,
  DraftType,
  ResolvePlan,
} from '@fsp/draft';

/**
 * An auction as a mock practices it (AUCTION.md §6.4): the league's money,
 * and a live auction's clocks even when the league's own run in hours, one
 * player at a time. A mock is an evening's practice, never a week's.
 */
export function practiceAuction(rules: AuctionRules): AuctionRules {
  if (rules.bidSeconds < 3600 && rules.nominationSeconds < 3600) return { ...rules };
  return {
    ...rules,
    nominationSeconds: 30,
    bidSeconds: 20,
    resetSeconds: 10,
    lotsAtOnce: 1,
  };
}

/** One seat, as the room shows it to everybody. `team.isMine` is false here; the room sets it per viewer. */
export interface LiveSeat {
  teamId: string;
  /** 1-based, in round-one order. */
  slot: number;
  team: TeamRef;
  /** Who manages it, or null for a seat a bot holds or nobody has taken. */
  ownerUserId: string | null;
  /** Its co-managers (B13): they draft for it and hear its turn too. Absent is none. */
  coManagerIds?: readonly string[];
  /** The bot in the seat, how it drafts and what it is called on the seat. */
  bot: { manager: AiManager; difficulty: string } | null;
}

/** One player on the board, with what a drafter reads about him (PLAN-2026-10-14 §3). */
export interface LivePlayer {
  id: string;
  summary: PlayerSummary;
  /** His primary position, the board's. */
  position: string;
  rank: number;
  positionRank: number;
  tier: number;
  bye: number | null;
  last: DraftSeasonLine | null;
  /**
   * What he is worth to this league's rosters over the player it could have
   * had instead, the basis the draft is ranked on and the recap grades by.
   */
  value: number;
  /**
   * What a bot's draft style knows about him (AI_MANAGERS.md §4.7): his club,
   * bye, rookie season, injury, games and first name. With the player rather
   * than in the context, so a big pool stays in the host's pieces. Absent from
   * a room prepared before 2026-10-15.
   */
  facts?: DraftFacts;
}

/** One of the league's roster slots, in the league's order. */
export interface LiveSlot {
  slot: string;
  slotName: string;
  count: number;
  /** Positions it takes; empty for any. */
  eligible: string[];
  kind: string;
}

/** Everything the room needs that is not the draft's own state. */
export interface LiveDraftPrepared {
  leagueId: string;
  draftId: string;
  /** The `drafts` row's status when this was read. */
  draftStatus: string;
  /** The league as everybody sees it; the room fills in each viewer's own team and role. */
  league: LeagueRef;
  mode: DraftMode;
  type: DraftType;
  rounds: number;
  pickSeconds: number;
  whenTheClockExpires: ClockExpiry;
  scheduledAt: string | null;
  startedAt: string | null;
  order: string[];
  /**
   * The order is settled: stored when the draft began, or set by the
   * commissioner. Until then `order` is today's seeded shuffle, which moves
   * when somebody joins.
   */
  orderFixed: boolean;
  seats: LiveSeat[];
  /** The whole pool, in the order the draft runs on. */
  players: LivePlayer[];
  basis: string;
  /** Each pro club's bye this season (`DraftView.byeWeeks`). */
  byeWeeks: Record<string, number>;
  context: DraftContextParts;
  slots: LiveSlot[];
  /** Starting slots that take one position, and how many: what "needs" are counted against. */
  needs: { position: string; count: number }[];
  commissionerIds: string[];
  preparedAt: string;
  /**
   * The hours the pick clock does not run, in `timeZone` (C7, B-341); null
   * for a clock that never stops. Absent from a room prepared before
   * 2026-10-15, which has none.
   */
  overnightPause?: { from: string; to: string } | null;
  /** The league's zone, which the overnight pause is told in. */
  timeZone?: string;
  /**
   * The league trades during its draft (`draft.pickTradingAllowed`, DRAFT_ROOM.md
   * "Trading during the draft"). Absent from a room prepared before
   * 2026-10-15, which reads as no.
   */
  trading?: boolean;
  /**
   * An auction's rules (AUCTION.md): the room runs one, nominating in `order`
   * round and round, and `type` is `linear`, its board's. Absent or null for a
   * pick draft, as every room prepared before 2026-10-15 reads.
   */
  auction?: AuctionRules | null;
  /**
   * Last season's keepers (PLAN-2026-10-27-the-next-season §2.3): each on the
   * round of its team's own pick, or bought at its price in an auction, out
   * of the pool from the start. Absent where nobody keeps anybody, as every
   * room prepared before 2026-10-27 reads.
   */
  keepers?: DraftKeeper[];
  /**
   * A dynasty's carried rosters (PLAN-2026-10-27-the-next-season §2.4 rule
   * 4): each team's players from last season, by team id (`DraftState.held`),
   * out of the pool and counted in its needs, a mock's as well as the
   * draft's. Absent where nobody carries anybody, as every room prepared
   * before 2026-10-27 reads.
   */
  held?: Record<string, string[]>;
  /**
   * The picks that changed hands before the draft, in the league's own trades
   * (PLAN-2026-10-27-the-next-season §2.6 rule 5): `round:teamId` of the
   * slot's own team to the team that holds it (`DraftState.tradedBefore`).
   * The league's draft's alone, never a mock's. Absent: none.
   */
  owners?: Record<string, string>;
  /** Each carried player as the room shows him on his team; on no board. */
  carried?: LivePlayer[];
  /**
   * A league mock's room (MOCK_DRAFT.md §6.2), not the league's draft: its
   * seats are taken in the lobby, whoever opened it runs it, and the
   * priorities a person's seat drafts from on auto are theirs, by person,
   * until the start puts each on the seat its person took. Absent for the
   * league's own room.
   */
  mock?: {
    mockId: string;
    hostUserId: string;
    hostName: string;
    /** When a lobby nobody starts closes (ISO). */
    closesAt: string;
    plansByUser: Record<string, ResolvePlan>;
  };
}
